import { useEffect } from 'react';

import { create } from 'zustand';

import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { Logger } from 'src/common/services/Logger';

import { processPlatformConfiguration } from '../helpers/ConfigurationProcessingHelpers';
import { sanitizeConfigurationLabel } from '../helpers/DataSanitizationHelpers';
import { BusinessUnit } from '../types/DashboardTypes';

interface IDashboardConfigStore {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;

	// Actions
	setBusinessUnits: (units: BusinessUnit[]) => void;
	clearBusinessUnits: () => void;

	// Selectors
	getBusinessUnitById: (id: string) => BusinessUnit | null;
	getFirstBusinessUnit: () => BusinessUnit | null;
}

export const useDashboardConfigStore = create<IDashboardConfigStore>((set, get) => ({
	businessUnits: [],
	isLoading: false,
	error: null,

	setBusinessUnits: (units: BusinessUnit[]) =>
		set({
			businessUnits: units,
			isLoading: false,
			error: null
		}),

	clearBusinessUnits: () =>
		set({
			businessUnits: [],
			isLoading: false,
			error: null
		}),

	getBusinessUnitById: (id: string) => {
		const { businessUnits } = get();
		return businessUnits.find((unit) => unit.id === id) || null;
	},

	getFirstBusinessUnit: () => {
		const { businessUnits } = get();
		return businessUnits.length > 0 ? businessUnits[0] : null;
	}
}));

export const useBusinessUnitsFromConfig = (): {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;
} => {
	const { configuration } = useConfiguration();
	const { businessUnits, setBusinessUnits, error } = useDashboardConfigStore();

	// Extract business units from configuration when it's available
	useEffect(() => {
		if (!configuration) {
			return;
		}

		if (businessUnits.length > 0) {
			return;
		}

		if (!configuration.PLATFORM) {
			Logger.warn('No PLATFORM configuration found');
			setBusinessUnits([]);
			return;
		}

		try {
			const { coreLabels } = processPlatformConfiguration(configuration.PLATFORM);
			const businessUnitLabel = coreLabels.find((label) => label.label === 'unitId');

			if (!businessUnitLabel) {
				Logger.warn('No unitId label found in configuration');
				setBusinessUnits([]);
				return;
			}

			const sanitized = sanitizeConfigurationLabel(businessUnitLabel);

			if (sanitized.values.length > 0) {
				const units = sanitized.values.map((value) => ({
					id: value,
					name: value
				}));
				setBusinessUnits(units);
			} else {
				setBusinessUnits([]);
			}
		} catch (error) {
			Logger.error('Failed to extract business units from configuration:', error);
			setBusinessUnits([]);
		}
	}, [configuration, businessUnits.length, setBusinessUnits]);

	const actualIsLoading = !configuration && businessUnits.length === 0;

	return {
		businessUnits,
		isLoading: actualIsLoading,
		error
	};
};
